import axios from 'axios';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';
import type { Account } from '@/services/accounts';
import type { Institution } from '@/services/institutions';
import type { PaginatedResponse } from '@/services/_base';
import { getApiEndpoint } from '@/globals/api-endpoint';
import { session } from '@/globals/session';

const DAY_MS = 24 * 60 * 60 * 1000;
const RETRY_MS = 5 * 60 * 1000;

interface RefreshContext {
  token: string;
  endpoint: string | undefined;
  account: Account;
  institution: Institution;
  generation: number;
}

let lastRefresh: { context: RefreshContext; at: number } | undefined;
let inFlight: { context: RefreshContext; promise: Promise<boolean> } | undefined;

function getContext(): RefreshContext | undefined {
  const token = localStorage.getItem('accessToken');
  if (!token || !session.account || !session.institution) return;
  if (localStorage.accessMode === 'Device' || localStorage.deviceConfig) return;
  return {
    token,
    endpoint: getApiEndpoint(),
    account: session.account,
    institution: session.institution,
    generation: session.generation,
  };
}

function sameContext(left: RefreshContext, right?: RefreshContext): boolean {
  return !!right && left.token === right.token && left.endpoint === right.endpoint &&
    left.account === right.account && left.institution === right.institution &&
    left.generation === right.generation;
}

function isFresh(context: RefreshContext): boolean {
  if (!lastRefresh || !sameContext(lastRefresh.context, context)) return false;
  const now = new Date();
  const age = now.getTime() - lastRefresh.at;
  return age >= 0 && age < DAY_MS && new Date(lastRefresh.at).toDateString() === now.toDateString();
}

async function loadSession(context: RefreshContext): Promise<boolean> {
  try {
    // Isolated requests bypass service caches and the shared 403 interceptor:
    // a response for an old token must never log out a newly switched session.
    const config = {
      baseURL: context.endpoint,
      timeout: 10000,
      headers: { Authorization: `Bearer ${context.token}` },
    };
    const [accounts, institutions] = await Promise.all([
      axios.get<PaginatedResponse<Account>>('/api/v2/accounts/users/?limit=1', config),
      axios.get<PaginatedResponse<Institution>>('/api/v2/accounts/institutions/?limit=1', config),
    ]);
    const account = accounts?.data?.results?.[0];
    const institution = institutions?.data?.results?.[0];
    if (!sameContext(context, getContext()) || !account || !institution) return false;
    if (account.id !== context.account.id || institution.id !== context.institution.id ||
        String(account.institution_id) !== String(institution.id)) return false;

    // Freshness is deliberately memory-only: every app launch verifies snapshots
    // restored from localStorage, including those predating new server fields.
    // Set it before the atomic change event so listeners can reuse this result.
    lastRefresh = { context: { ...context, account, institution }, at: Date.now() };
    session.setSession(account, institution);
    return true;
  } catch {
    // Preserve the offline session. A failed attempt never counts as a refresh.
    return false;
  }
}

export function refreshSessionIfNeeded(): Promise<boolean> {
  const context = getContext();
  if (!context) return Promise.resolve(false);
  if (isFresh(context)) return Promise.resolve(true);
  if (inFlight && sameContext(inFlight.context, context)) return inFlight.promise;

  const promise = loadSession(context);
  inFlight = { context, promise };
  void promise.finally(() => {
    if (inFlight?.promise === promise) inFlight = undefined;
  });
  return promise;
}

export function startSessionRefresh(): () => void {
  let stopped = false;
  let queued = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let nativeListener: PluginListenerHandle | undefined;

  function scheduleNext() {
    if (stopped) return;
    if (timer !== undefined) clearTimeout(timer);
    const now = new Date();
    const midnight = new Date(now);
    midnight.setHours(24, 0, 0, 0);
    const context = getContext();
    const delay = context && !isFresh(context) ? RETRY_MS : DAY_MS;
    timer = setTimeout(wake, Math.min(midnight.getTime() - now.getTime(), delay));
  }

  function wake() {
    if (stopped) return;
    // Prompt listeners await the same deduplicated request before reevaluating.
    session.emit('resume');
    void refreshSessionIfNeeded().finally(scheduleNext);
  }

  function onSessionChange() {
    if (queued || stopped) return;
    queued = true;
    queueMicrotask(() => {
      queued = false;
      wake();
    });
  }

  function onVisibilityChange() {
    if (document.visibilityState === 'visible') wake();
  }

  session.on('change', onSessionChange);
  session.on('reset', onSessionChange);
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('focus', wake);
  window.addEventListener('pageshow', wake);
  window.addEventListener('online', wake);
  if (Capacitor.isNativePlatform()) {
    void App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) wake();
    }).then(handle => {
      if (stopped) void handle.remove();
      else nativeListener = handle;
    }).catch(() => { /* Native lifecycle hooks may be unavailable in a WebView. */ });
  }
  wake();

  return () => {
    stopped = true;
    if (timer !== undefined) clearTimeout(timer);
    session.off('change', onSessionChange);
    session.off('reset', onSessionChange);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('focus', wake);
    window.removeEventListener('pageshow', wake);
    window.removeEventListener('online', wake);
    if (nativeListener) void nativeListener.remove();
  };
}
